export interface SRSCard {
  interval: number;        // days
  ease_factor: number;     // e.g. 2.5
  repetitions: number;     // count of consecutive correct reviews
  next_review_date: Date;  // exact date/time
}

export type SRSResponse = "AGAIN" | "HARD" | "GOOD" | "EASY" | "again" | "hard" | "good" | "easy";

/**
 * Calculates new interval, ease factor, and repetitions based on the classic SM-2 algorithm.
 */
export function updateCardSRS(
  card: SRSCard,
  userResponse: SRSResponse,
  currentDate: Date = new Date()
): SRSCard {
  const updatedCard: SRSCard = {
    interval: card.interval ?? 0,
    ease_factor: card.ease_factor ?? 2.5,
    repetitions: card.repetitions ?? 0,
    next_review_date: new Date(card.next_review_date ?? currentDate)
  };

  const response = userResponse.toUpperCase() as "AGAIN" | "HARD" | "GOOD" | "EASY";

  switch (response) {
    case "AGAIN":
      updatedCard.repetitions = 0;
      updatedCard.interval = 0; // Reappear in the same session / today
      updatedCard.ease_factor = Math.max(1.3, updatedCard.ease_factor - 0.20);
      break;

    case "HARD":
      updatedCard.repetitions += 1;
      updatedCard.ease_factor = Math.max(1.3, updatedCard.ease_factor - 0.15);
      
      if (updatedCard.repetitions === 1) {
        updatedCard.interval = 1;
      } else if (updatedCard.repetitions === 2) {
        updatedCard.interval = 3;
      } else if (updatedCard.repetitions > 2) {
        updatedCard.interval = Math.round(updatedCard.interval * 1.2);
      }
      break;

    case "GOOD":
      if (updatedCard.repetitions === 0) {
        updatedCard.interval = 1;
      } else if (updatedCard.repetitions === 1) {
        updatedCard.interval = 4;
      } else if (updatedCard.repetitions > 1) {
        updatedCard.interval = Math.round(updatedCard.interval * updatedCard.ease_factor);
      }
      updatedCard.repetitions += 1;
      break;

    case "EASY":
      updatedCard.ease_factor = updatedCard.ease_factor + 0.15;
      
      if (updatedCard.repetitions === 0) {
        updatedCard.interval = 4;
      } else if (updatedCard.repetitions > 0) {
        updatedCard.interval = Math.round(updatedCard.interval * updatedCard.ease_factor * 1.3);
      }
      updatedCard.repetitions += 1;
      break;
  }

  // Ensure ease factor floor of 1.3 is preserved
  if (updatedCard.ease_factor < 1.3) {
    updatedCard.ease_factor = 1.3;
  }

  // Calculate next review date: currentDate + interval (in days)
  const nextDate = new Date(currentDate);
  nextDate.setDate(nextDate.getDate() + updatedCard.interval);
  updatedCard.next_review_date = nextDate;

  return updatedCard;
}

/**
 * Helper function to estimate interval for legacy cards without metadata.interval.
 */
export function estimateExistingInterval(card: {
  repetitions?: number;
  nextReviewAt?: string;
  lastReviewedAt?: string;
  createdAt?: string;
  metadata?: any;
}): number {
  if (card.metadata?.interval !== undefined && card.metadata?.interval !== null) {
    const parsed = Number(card.metadata.interval);
    if (!isNaN(parsed)) return parsed;
  }

  const reps = card.repetitions ?? card.metadata?.repetitions ?? 0;
  if (reps === 0) return 0;
  if (reps === 1) return 1;

  // Try to calculate from actual dates
  const nextDateStr = card.nextReviewAt || card.metadata?.nextReviewAt;
  const lastDateStr = card.lastReviewedAt || card.metadata?.lastReviewedAt || card.createdAt || card.metadata?.createdAt;

  if (nextDateStr && lastDateStr) {
    const end = new Date(nextDateStr);
    const start = new Date(lastDateStr);
    if (!isNaN(end.getTime()) && !isNaN(start.getTime())) {
      const diffMs = end.getTime() - start.getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays > 0) {
        return diffDays;
      }
    }
  }

  // Heuristic fallbacks based on repetitions
  if (reps === 2) return 4;
  return 1; // Safe restart fallback
}
